import type { JSX } from 'react';
import { useState } from 'react';
import { Alert, Button, Link, Stack, TextField, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import { AuthClientError } from '../api/auth-http-client';
import { useAuth } from './AuthProvider';
import { AuthShell } from './AuthShell';

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();

  const from =
    (location.state as { from?: { pathname?: string } } | undefined)?.from?.pathname ?? '/';
  const prefillEmail = new URLSearchParams(location.search).get('email') ?? '';

  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      await auth.login(email, password);
    },
    onSuccess: () => {
      navigate(from);
    },
    onError: (error) => {
      const message =
        error instanceof AuthClientError
          ? error.apiError?.message ?? error.message
          : error instanceof Error
            ? error.message
            : 'Unable to sign in. Please try again.';
      setFormError(message);
    },
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setFormError(null);
    mutation.mutate();
  };

  return (
    <AuthShell title="Sign in to continue" subtitle="Use your CRV Systems account.">
      <form onSubmit={handleSubmit} noValidate>
        <Stack spacing={2.5}>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
            autoComplete="email"
            required
            fullWidth
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
            autoComplete="current-password"
            required
            fullWidth
          />

          {formError && <Alert severity="error">{formError}</Alert>}

          <Button type="submit" variant="contained" size="large" disabled={mutation.isPending}>
            {mutation.isPending ? 'Signing in...' : 'Sign in'}
          </Button>

          <Typography variant="body2" color="text.secondary">
            Need an account?{' '}
            <Link component={NavLink} to="/signup" underline="hover">
              Create one
            </Link>
          </Typography>
        </Stack>
      </form>
    </AuthShell>
  );
}
